import {
  BookingVipLevel,
  GuestVipLevel,
  MembershipStatus,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { GuestInfo } from '@new/__global/components/GuestCard'
import { dollarToCents } from '@new/domains/payments/utils/dollarToCents'

import {
  AdditionalEmailsProps,
  EmailsToUpdateProps,
  GuestBooking,
  GuestBookingCustomFields,
  GuestMembershipInfo,
} from './Guests.types'
import { ConflictingGuest } from './stores/api/createGuestApi'

const guestDamageCustomFieldsIds = {
  section: 'e4b1d5be-6210-11ea-bf0a-e9902fdb3b47',
  guestDamageField: '5ed4ecbf-6211-11ea-bf0a-d9ecd31d37c0',
  petDamageField: 'da1709ec-9d92-4798-bbb2-7d00f6c95e91',
  wasClaimPaidOut: '5ed4ecc2-6211-11ea-bf0a-73b3d7e76ef5',
}

const refundsCustomFieldsIds = {
  section: '520d30cc-1d40-11ea-8f7f-0dd515c3c11f',
  amountRefunded: '9a64c08d-2117-11ea-b978-137c044f5697',
  refundReasons: 'b3d84c5e-1d44-11ea-8f7f-6dd8482f4c18',
}

const getCustomFieldById = (customField: GuestBookingCustomFields | undefined, id: string) =>
  customField?.fields.find(innerField => innerField.id === id)

export const getDamageStatus = (booking: GuestBooking) => {
  const damageCustomField = booking.customFields.find(
    field => field.section.id === guestDamageCustomFieldsIds.section,
  )

  const guestDamageField = getCustomFieldById(
    damageCustomField,
    guestDamageCustomFieldsIds.guestDamageField,
  )

  const petDamageField = getCustomFieldById(
    damageCustomField,
    guestDamageCustomFieldsIds.petDamageField,
  )

  const hasDamage = guestDamageField?.value === 'true' || petDamageField?.value === 'true'

  const damageHasBeenPaid =
    getCustomFieldById(damageCustomField, guestDamageCustomFieldsIds.wasClaimPaidOut)?.value ===
    'true'

  return {
    hasDamage,
    damageHasBeenPaid,
  }
}

export const getRefunds = (booking: GuestBooking) => {
  const refundCustomField = booking.customFields.find(
    f => f.section.id === refundsCustomFieldsIds.section,
  )

  const amountRefundedField = getCustomFieldById(
    refundCustomField,
    refundsCustomFieldsIds.amountRefunded,
  )

  const refundReasonsField = getCustomFieldById(
    refundCustomField,
    refundsCustomFieldsIds.refundReasons,
  )

  return {
    total:
      amountRefundedField?.value && !Number.isNaN(Number(amountRefundedField?.value))
        ? dollarToCents(amountRefundedField?.value)
        : undefined,
    reasons: refundReasonsField?.value ? JSON.parse(refundReasonsField?.value) : undefined,
  }
}

export const toGuestInfo = (guest: ConflictingGuest): GuestInfo => ({
  userId: guest.userId,
  guestId: guest.guestId,
  name: guest.name,
  email: guest.emails[0],
  phone: guest.phones[0],
  type: guest.kind?.type,
  stays: guest.stays.total,
})

export const isVip = (vipLevel?: GuestVipLevel | BookingVipLevel) =>
  vipLevel === GuestVipLevel.vip1 || vipLevel === GuestVipLevel.vip2

export interface GetEmailsToUpdateProps {
  additionalEmails?: AdditionalEmailsProps[]
  originalEmails: AdditionalEmailsProps[]
}

export const getEmailsToUpdate = ({ additionalEmails, originalEmails }: GetEmailsToUpdateProps) => {
  const formattedAdditionalEmails = additionalEmails?.map(email => email.value) ?? []
  const deletedEmails = originalEmails.reduce((accDeletedEmails: string[], currentEmail) => {
    return !formattedAdditionalEmails.includes(currentEmail.value)
      ? [...accDeletedEmails, currentEmail.value]
      : accDeletedEmails
  }, [])

  const emailsToUpdate = additionalEmails?.reduce(
    (accEmailsToUpdate: EmailsToUpdateProps, currentEmail) => {
      if (currentEmail.originalEmail == null) {
        return {
          emailsToAdd: [...accEmailsToUpdate.emailsToAdd, currentEmail.value],
          emailsToRemove: accEmailsToUpdate.emailsToRemove,
        }
      }

      if (currentEmail.originalEmail !== currentEmail.value) {
        return {
          emailsToAdd: [...accEmailsToUpdate.emailsToAdd, currentEmail.value],
          emailsToRemove: accEmailsToUpdate.emailsToRemove.filter(
            emailToRemove => emailToRemove !== currentEmail.value,
          ),
        }
      }

      return accEmailsToUpdate
    },
    {
      emailsToAdd: [],
      emailsToRemove: [],
    },
  )

  const emailsToRemove = [...deletedEmails, ...(emailsToUpdate?.emailsToRemove ?? [])]

  return {
    emailsToAdd: emailsToUpdate?.emailsToAdd ?? [],
    emailsToRemove,
  }
}

interface ResolveDisplayedMembershipParams {
  membershipInfo?: GuestMembershipInfo
  membershipHistory?: GuestMembershipInfo[]
}

export const resolveDisplayedMembership = ({
  membershipInfo,
  membershipHistory,
}: ResolveDisplayedMembershipParams) => {
  if (membershipInfo) return membershipInfo

  if (!membershipHistory?.length) return undefined

  const activeMembership = membershipHistory.find(
    membership => membership.status === MembershipStatus.active,
  )

  if (activeMembership) return activeMembership

  const sortedMembershipHistory = membershipHistory.toSorted(
    (a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime(),
  )

  return sortedMembershipHistory[0]
}
